import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarRange,
  ChevronsUpDown,
  Clock3,
  Plus,
  RefreshCcw,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { getCrmErrorMessage } from '../utils/crmErrors';
import {
  calculateMinutesFromTimes,
  createCrmMeeting,
  fetchCrmMeetingOptions,
  formatMinutes,
} from '../utils/crmWorkflow';
import CrmFieldLabel from '../components/crm/CrmFieldLabel';

const MEETING_TOOLTIPS = {
  title: 'Nombre operativo de la reunion para identificarla rapido en agenda, historial y reportes.',
  participants: 'Participantes generales de la reunion. Se usarán para distribuir tiempo en los temas cuando no haya participantes especificos.',
  date: 'Fecha en la que ocurre la reunion y desde la cual se imputan las horas procesadas.',
  start_time: 'Hora real o estimada de inicio de la reunion.',
  end_time: 'Hora real o estimada de cierre. Define la duracion total disponible para distribuir.',
  duration: 'Duracion calculada a partir del inicio y la finalizacion de la reunion.',
  agenda: 'Contexto general, objetivo de la reunion y puntos que todo el equipo deberia conocer.',
  project: 'Proyecto al que se van a imputar las horas de este tema cuando proceses la reunion.',
  client: 'Cliente asociado al proyecto del tema para revisar rapido el contexto comercial.',
  topic_participants: 'Participantes exclusivos de este tema. Si lo dejas vacio, se usan los participantes generales.',
  topic_minutes: 'Minutos reales consumidos por este tema. Si queda vacio, el sistema reparte el tiempo restante automaticamente.',
  topic_notes: 'Notas del tema, acuerdos o foco esperado para quien procese la reunion despues.',
  treated: 'Marca si este tema efectivamente se trato en la reunion. Solo los tratados generan imputacion de horas.',
};

function buildTopicDraft() {
  return {
    project_id: '',
    title: '',
    notes: '',
    is_treated: false,
    topic_minutes: '',
    topic_participant_ids: [],
  };
}

function buildInitialMeetingForm(profileId = '') {
  return {
    title: '',
    agenda_text: '',
    meeting_date: new Date().toISOString().slice(0, 10),
    start_time: '09:00',
    end_time: '10:00',
    participant_ids: profileId ? [profileId] : [],
    topics: [buildTopicDraft()],
  };
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="bg-[#0B2A36] rounded-t-2xl px-4 py-3 border-b border-white/10 flex items-center gap-3">
      <Icon size={16} className="text-[#3FF2AA] shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] font-black uppercase tracking-widest text-white truncate">{title}</p>
        {subtitle ? <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">{subtitle}</p> : null}
      </div>
    </div>
  );
}

function getProfileDisplayName(user) {
  return user?.full_name || user?.email || 'Usuario';
}

function getTopicDisplayName(topic, topicIndex, projects = []) {
  const selectedProject = (projects || []).find((projectRow) => projectRow.id === topic?.project_id);
  if (selectedProject?.code || selectedProject?.name) {
    return `${selectedProject.code ? `#${selectedProject.code} ` : ''}${selectedProject.name || 'Sin nombre'}`;
  }
  if (topic?.title?.trim()) return topic.title.trim();
  return `Tema ${topicIndex + 1}`;
}

function summarizeSelectedProfiles(selectedIds, profiles, emptyLabel = 'Seleccionar participantes') {
  const selectedProfiles = (profiles || []).filter((user) => (selectedIds || []).includes(user.id));
  if (!selectedProfiles.length) return emptyLabel;
  if (selectedProfiles.length === 1) return getProfileDisplayName(selectedProfiles[0]);
  if (selectedProfiles.length === 2) return selectedProfiles.map(getProfileDisplayName).join(' - ');
  return `${selectedProfiles.length} participantes`;
}

function getProjectDisplayLabel(project, clientMap = {}) {
  if (!project) return 'Seleccionar proyecto';
  return `${project.code ? `#${project.code} - ` : ''}${project.name || 'Sin nombre'} - ${clientMap[project.client_id]?.full_name || 'Sin cliente'}`;
}

function ParticipantPicker({
  profiles,
  selectedIds,
  onToggle,
  disabled,
  emptyLabel = 'Seleccionar participantes',
  getOptionMeta,
}) {
  const [query, setQuery] = useState('');

  const filteredProfiles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return profiles || [];

    return (profiles || []).filter((user) => {
      const haystack = `${user.full_name || ''} ${user.email || ''}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [profiles, query]);

  const summary = useMemo(
    () => summarizeSelectedProfiles(selectedIds, profiles, emptyLabel),
    [emptyLabel, profiles, selectedIds],
  );

  return (
    <details className="group relative">
      <summary className="list-none w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black outline-none cursor-pointer flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="block truncate text-slate-700">{summary}</span>
        </div>
        <Users size={14} className="shrink-0 text-slate-400 group-open:text-[#51A7FD]" />
      </summary>

      <div className="absolute z-30 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-xl p-2">
        <div className="mb-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:border-[#51A7FD]"
            placeholder="Buscar usuario"
            disabled={disabled}
          />
        </div>

        <div className="space-y-1 max-h-[280px] overflow-y-auto pr-1">
          {filteredProfiles.length ? filteredProfiles.map((user) => {
            const checked = (selectedIds || []).includes(user.id);
            const optionMeta = getOptionMeta ? (getOptionMeta(user) || {}) : {};
            const optionDisabled = Boolean(disabled || optionMeta.disabled);

            return (
              <label
                key={user.id}
                title={optionMeta.reason || undefined}
                className={`flex items-center gap-3 rounded-xl border px-3 py-3 transition-all ${optionDisabled ? 'cursor-help opacity-65' : 'cursor-pointer'} ${checked ? 'bg-[#F2F9FF] border-[#51A7FD]/30' : 'bg-white border-slate-200'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(user.id)}
                  className="accent-[#51A7FD]"
                  disabled={optionDisabled}
                />
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase text-slate-800 truncate">{getProfileDisplayName(user)}</p>
                  <p className={`text-[9px] font-bold truncate ${optionMeta.reason ? 'text-amber-600' : 'text-slate-400'}`}>
                    {optionMeta.reason || user.email}
                  </p>
                </div>
              </label>
            );
          }) : (
            <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Sin coincidencias
            </div>
          )}
        </div>
      </div>
    </details>
  );
}

function ProjectPicker({
  projects,
  clientMap,
  value,
  onChange,
  disabled,
  emptyLabel = 'Seleccionar proyecto',
  getOptionMeta,
}) {
  const [query, setQuery] = useState('');

  const selectedProject = useMemo(
    () => (projects || []).find((projectRow) => projectRow.id === value) || null,
    [projects, value],
  );

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return projects || [];

    return (projects || []).filter((projectRow) => {
      const haystack = `${projectRow.code || ''} ${projectRow.name || ''} ${clientMap[projectRow.client_id]?.full_name || ''}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [clientMap, projects, query]);

  return (
    <details className="group relative">
      <summary className="list-none w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-[11px] font-black outline-none cursor-pointer flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className={`block truncate ${selectedProject ? 'text-slate-700' : 'text-slate-400'}`}>
            {selectedProject ? getProjectDisplayLabel(selectedProject, clientMap) : emptyLabel}
          </span>
        </div>
        <ChevronsUpDown size={14} className="shrink-0 text-slate-400 group-open:text-[#51A7FD]" />
      </summary>

      <div className="absolute z-30 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-xl p-2">
        <div className="mb-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:border-[#51A7FD]"
            placeholder="Buscar proyecto"
            disabled={disabled}
          />
        </div>

        {selectedProject ? (
          <button
            type="button"
            onClick={() => onChange('')}
            className="mb-2 w-full rounded-xl border border-dashed border-slate-200 px-3 py-2 text-left text-[10px] font-black uppercase tracking-widest text-slate-500 hover:border-slate-300"
            disabled={disabled}
          >
            Limpiar seleccion
          </button>
        ) : null}

        <div className="space-y-1 max-h-[280px] overflow-y-auto pr-1">
          {filteredProjects.length ? filteredProjects.map((projectRow) => {
            const selected = value === projectRow.id;
            const optionMeta = getOptionMeta ? (getOptionMeta(projectRow) || {}) : {};
            const optionDisabled = Boolean(disabled || optionMeta.disabled);

            return (
              <button
                key={projectRow.id}
                type="button"
                onClick={() => {
                  if (optionDisabled) return;
                  onChange(projectRow.id);
                }}
                aria-disabled={optionDisabled}
                title={optionMeta.reason || undefined}
                className={`w-full text-left rounded-xl border px-3 py-3 transition-all ${optionDisabled ? 'cursor-help bg-slate-100 border-slate-200 text-slate-400' : selected ? 'bg-[#F2F9FF] border-[#51A7FD]/30 text-slate-800' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'}`}
                disabled={disabled}
              >
                <p className="text-[10px] font-black uppercase truncate">
                  {getProjectDisplayLabel(projectRow, clientMap)}
                </p>
                <p className={`mt-1 text-[9px] font-bold truncate ${optionMeta.reason ? 'text-amber-600' : 'text-slate-400'}`}>
                  {optionMeta.reason || 'Disponible para agregar'}
                </p>
              </button>
            );
          }) : (
            <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Sin coincidencias
            </div>
          )}
        </div>
      </div>
    </details>
  );
}

function validateMeetingForm(form) {
  if (!form.title.trim()) return 'Completa el titulo de la reunion';
  if (!form.meeting_date) return 'Completa la fecha de la reunion';
  if (!form.start_time) return 'Completa la hora de inicio';
  if (!form.end_time) return 'Completa la hora de finalizacion';

  const durationMinutes = calculateMinutesFromTimes(form.start_time, form.end_time);
  if (!durationMinutes) return 'La hora de finalizacion debe ser posterior al inicio';

  if (!form.participant_ids.length) return 'Selecciona al menos un participante';

  const activeTopics = (form.topics || []).filter((topic) => (
    topic.project_id
    || topic.title.trim()
    || topic.notes.trim()
    || (topic.topic_participant_ids || []).length
  ));

  if (!activeTopics.length) return 'Agrega al menos un tema vinculado a un proyecto';

  let explicitMinutes = 0;

  for (const topic of activeTopics) {
    if (!topic.project_id) return 'Todos los temas deben estar vinculados a un proyecto';

    const topicMinutes = topic.topic_minutes === '' ? null : Number(topic.topic_minutes || 0);

    if (topicMinutes != null && topicMinutes <= 0) {
      return 'El tiempo de tema debe ser mayor a cero';
    }

    if (topicMinutes != null && !Number.isNaN(topicMinutes)) {
      explicitMinutes += topicMinutes;
    }
  }

  if (explicitMinutes > durationMinutes) {
    return 'La suma de tiempos de tema no puede superar el total de la reunion';
  }

  return null;
}

export default function AgendaNueva() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [meetingForm, setMeetingForm] = useState(() => buildInitialMeetingForm(profile?.id || ''));
  const [options, setOptions] = useState({ clients: [], profiles: [], tickets: [], projects: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meetingParticipantConflict, setMeetingParticipantConflict] = useState(null);
  const [topicDeleteConfirm, setTopicDeleteConfirm] = useState(null);

  const loadOptions = useCallback(async () => {
    if (!profile?.organization_id) return;

    setLoading(true);
    try {
      const nextOptions = await fetchCrmMeetingOptions(profile.organization_id);
      setOptions(nextOptions);
    } catch (error) {
      console.error(error);
      toast.error(getCrmErrorMessage(error, 'No se pudo preparar el alta de reuniones'), { id: 'crm-meeting-create-load-error' });
    } finally {
      setLoading(false);
    }
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadOptions();
  }, [loadOptions, profile?.organization_id]);

  useEffect(() => {
    if (!profile?.id) return;
    setMeetingForm((current) => ({
      ...current,
      participant_ids: current.participant_ids.length ? current.participant_ids : [profile.id],
    }));
  }, [profile?.id]);

  const projectMap = useMemo(
    () => Object.fromEntries((options.projects || []).map((project) => [project.id, project])),
    [options.projects],
  );
  const clientMap = useMemo(
    () => Object.fromEntries((options.clients || []).map((client) => [client.id, client])),
    [options.clients],
  );

  const selectedMeetingClientId = useMemo(() => {
    const selectedTopicClientIds = [...new Set((meetingForm.topics || [])
      .map((topic) => projectMap[topic.project_id]?.client_id || null)
      .filter(Boolean))];

    // Use the single client if all topics share one; otherwise fall back to the first topic's client
    if (selectedTopicClientIds.length === 1) return selectedTopicClientIds[0];
    if (selectedTopicClientIds.length > 1) return selectedTopicClientIds[0];
    return null;
  }, [meetingForm.topics, projectMap]);
  const durationPreview = useMemo(
    () => formatMinutes(calculateMinutesFromTimes(meetingForm.start_time, meetingForm.end_time)),
    [meetingForm.end_time, meetingForm.start_time],
  );
  const topicParticipantUsage = useMemo(() => (
    (meetingForm.topics || []).reduce((accumulator, topic, topicIndex) => {
      (topic.topic_participant_ids || []).forEach((profileId) => {
        accumulator[profileId] = accumulator[profileId] || [];
        accumulator[profileId].push(topicIndex + 1);
      });
      return accumulator;
    }, {})
  ), [meetingForm.topics]);

  useEffect(() => {
    if (!meetingParticipantConflict?.profileId) return;
    if ((topicParticipantUsage[meetingParticipantConflict.profileId] || []).length) return;
    setMeetingParticipantConflict(null);
  }, [meetingParticipantConflict, topicParticipantUsage]);

  const handleTopicChange = (index, field, value) => {
    setMeetingForm((current) => ({
      ...current,
      topics: current.topics.map((topic, topicIndex) => {
        if (topicIndex !== index) return topic;

        const nextTopic = { ...topic, [field]: value };

        if (field === 'project_id') {
          const selectedProject = (options.projects || []).find((projectRow) => projectRow.id === value);
          if (selectedProject && !topic.title.trim()) {
            nextTopic.title = selectedProject.name || topic.title;
          }
        }

        return nextTopic;
      }),
    }));
  };

  const handleToggleMeetingParticipant = (profileId) => {
    const profileName = getProfileDisplayName((options.profiles || []).find((user) => user.id === profileId));

    setMeetingForm((current) => {
      const nextIds = new Set(current.participant_ids);
      if (nextIds.has(profileId)) {
        nextIds.delete(profileId);
        return { ...current, participant_ids: Array.from(nextIds) };
      }

      const affectedTopics = (current.topics || [])
        .flatMap((topic, index) => (
          (topic.topic_participant_ids || []).includes(profileId)
            ? [getTopicDisplayName(topic, index, options.projects || [])]
            : []
        ));

      if (affectedTopics.length) {
        setMeetingParticipantConflict({
          profileId,
          profileName,
          affectedTopics,
        });
        return current;
      }

      nextIds.add(profileId);
      return { ...current, participant_ids: Array.from(nextIds) };
    });
  };

  const confirmMeetingParticipantConflict = () => {
    if (!meetingParticipantConflict?.profileId) return;

    setMeetingForm((current) => {
      const nextIds = new Set(current.participant_ids);
      nextIds.add(meetingParticipantConflict.profileId);

      return {
        ...current,
        participant_ids: Array.from(nextIds),
        topics: (current.topics || []).map((topic) => ({
          ...topic,
          topic_participant_ids: (topic.topic_participant_ids || []).filter((profileId) => profileId !== meetingParticipantConflict.profileId),
        })),
      };
    });

    setMeetingParticipantConflict(null);
  };

  const cancelMeetingParticipantConflict = () => {
    setMeetingParticipantConflict(null);
  };

  const requestTopicRemoval = (topicIndex) => {
    const topic = meetingForm.topics?.[topicIndex];
    if (!topic) return;

    setTopicDeleteConfirm({
      topicIndex,
      label: getTopicDisplayName(topic, topicIndex, options.projects || []),
    });
  };

  const cancelTopicRemoval = () => {
    setTopicDeleteConfirm(null);
  };

  const confirmTopicRemoval = () => {
    if (topicDeleteConfirm?.topicIndex == null) return;

    setMeetingForm((current) => ({
      ...current,
      topics: (current.topics || []).filter((_, topicIndex) => topicIndex !== topicDeleteConfirm.topicIndex),
    }));
    setTopicDeleteConfirm(null);
  };

  const handleToggleTopicParticipant = (topicIndex, profileId) => {
    setMeetingForm((current) => ({
      ...current,
      topics: current.topics.map((topic, index) => {
        if (index !== topicIndex) return topic;
        const nextIds = new Set(topic.topic_participant_ids || []);
        if (nextIds.has(profileId)) nextIds.delete(profileId);
        else nextIds.add(profileId);
        return {
          ...topic,
          topic_participant_ids: Array.from(nextIds),
        };
      }),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!profile?.organization_id) return;

    const validationError = validateMeetingForm(meetingForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const durationMinutes = calculateMinutesFromTimes(meetingForm.start_time, meetingForm.end_time);
    const topics = (meetingForm.topics || [])
      .filter((topic) => (
        topic.project_id
        || topic.title.trim()
        || topic.notes.trim()
        || (topic.topic_participant_ids || []).length
      ))
      .map((topic, index) => ({
        project_id: topic.project_id,
        title: topic.title.trim() || null,
        notes: topic.notes.trim() || null,
        topic_order: index + 1,
        is_treated: Boolean(topic.is_treated),
        topic_minutes: topic.topic_minutes === '' ? null : Number(topic.topic_minutes || 0),
        topic_participant_ids: [...new Set(topic.topic_participant_ids || [])],
      }));

    setSaving(true);
    try {
      await createCrmMeeting({
        organization_id: profile.organization_id,
        client_id: selectedMeetingClientId,
        title: meetingForm.title.trim(),
        agenda_text: meetingForm.agenda_text.trim() || null,
        meeting_date: meetingForm.meeting_date,
        start_time: meetingForm.start_time,
        end_time: meetingForm.end_time,
        duration_minutes: durationMinutes,
        organizer_id: profile.id,
        participants: [...new Set(meetingForm.participant_ids)].map((participantId) => ({
          profile_id: participantId,
          auto_log_time: true,
        })),
        topics,
      });

      toast.success('Reunion creada');
      navigate('/sistema/agenda');
    } catch (error) {
      console.error(error);
      toast.error(getCrmErrorMessage(error, 'No se pudo crear la reunion'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/sistema/agenda')}
            className="w-10 h-10 bg-white border border-slate-200 rounded-2xl flex items-center justify-center text-slate-500 shadow-sm"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <CalendarRange size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black uppercase tracking-tight text-slate-800 truncate">Crear reunion</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Define participantes, temas vinculados a proyectos y tiempos listos para el cierre operativo.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={loadOptions}
          className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
        >
          <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
          Sincronizar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-visible">
          <SectionTitle icon={CalendarRange} title="Datos de la reunion" subtitle="Agenda general y ventana horaria" />
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="xl:col-span-2">
              <CrmFieldLabel label="Titulo" tooltip={MEETING_TOOLTIPS.title} />
              <input
                value={meetingForm.title}
                onChange={(event) => setMeetingForm((current) => ({ ...current, title: event.target.value }))}
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black outline-none focus:border-[#51A7FD]"
                placeholder="Ej: Seguimiento semanal con cliente"
                disabled={loading || saving}
              />
            </div>

            <div className="xl:col-span-2">
              <CrmFieldLabel label="Participantes" tooltip={MEETING_TOOLTIPS.participants} />
              <ParticipantPicker
                profiles={options.profiles || []}
                selectedIds={meetingForm.participant_ids}
                onToggle={handleToggleMeetingParticipant}
                disabled={loading || saving}
              />
            </div>

            <div>
              <CrmFieldLabel label="Fecha" tooltip={MEETING_TOOLTIPS.date} />
              <input
                type="date"
                value={meetingForm.meeting_date}
                onChange={(event) => setMeetingForm((current) => ({ ...current, meeting_date: event.target.value }))}
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black outline-none focus:border-[#51A7FD]"
                disabled={loading || saving}
              />
            </div>

            <div>
              <CrmFieldLabel label="Hora de inicio" tooltip={MEETING_TOOLTIPS.start_time} />
              <input
                type="time"
                value={meetingForm.start_time}
                onChange={(event) => setMeetingForm((current) => ({ ...current, start_time: event.target.value }))}
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black outline-none focus:border-[#51A7FD]"
                disabled={loading || saving}
              />
            </div>

            <div>
              <CrmFieldLabel label="Hora de finalizacion" tooltip={MEETING_TOOLTIPS.end_time} />
              <input
                type="time"
                value={meetingForm.end_time}
                onChange={(event) => setMeetingForm((current) => ({ ...current, end_time: event.target.value }))}
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black outline-none focus:border-[#51A7FD]"
                disabled={loading || saving}
              />
            </div>

            <div>
              <CrmFieldLabel label="Duracion estimada" tooltip={MEETING_TOOLTIPS.duration} />
              <div className="w-full bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 text-[11px] font-black text-slate-600 flex items-center gap-2">
                <Clock3 size={14} className="text-slate-400" />
                {durationPreview}
              </div>
            </div>

            <div className="md:col-span-2 xl:col-span-4">
              <CrmFieldLabel label="Agenda" tooltip={MEETING_TOOLTIPS.agenda} />
              <textarea
                value={meetingForm.agenda_text}
                onChange={(event) => setMeetingForm((current) => ({ ...current, agenda_text: event.target.value }))}
                className="w-full min-h-[100px] resize-y bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-bold outline-none focus:border-[#51A7FD]"
                placeholder="Objetivo de la reunion y puntos de contexto"
                disabled={loading || saving}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-visible">
          <SectionTitle icon={Plus} title="Temas de la reunion" subtitle="Cada tema se vincula a un proyecto y puede tener tiempo propio" />
          <div className="p-4 space-y-3">
            {meetingForm.topics.map((topic, index) => {
              const selectedProject = projectMap[topic.project_id] || null;

              return (
                <div key={`meeting-topic-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 space-y-3 relative">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      Tema {index + 1}
                    </p>
                    <button
                      type="button"
                      onClick={() => requestTopicRemoval(index)}
                      className="w-8 h-8 rounded-xl border border-red-200 bg-white text-red-500 hover:bg-red-50 transition-colors flex items-center justify-center"
                      title="Eliminar tema"
                      disabled={loading || saving}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
                    <div className="xl:col-span-4">
                      <CrmFieldLabel label="Proyecto" tooltip={MEETING_TOOLTIPS.project} />
                      <ProjectPicker
                        projects={options.projects || []}
                        clientMap={clientMap}
                        value={topic.project_id}
                        onChange={(projectId) => handleTopicChange(index, 'project_id', projectId)}
                        disabled={loading || saving}
                      />
                      {selectedProject?.code ? (
                        <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                          Codigo {selectedProject.code}
                        </p>
                      ) : null}
                    </div>

                    <div className="xl:col-span-2">
                      <CrmFieldLabel label="Cliente" tooltip={MEETING_TOOLTIPS.client} />
                      <div className="w-full bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 text-[11px] font-black text-slate-600 min-h-[46px] flex items-center">
                        <span className="truncate">{clientMap[selectedProject?.client_id]?.full_name || 'Sin cliente'}</span>
                      </div>
                    </div>

                    <div className="xl:col-span-3">
                      <CrmFieldLabel label="Participantes del tema" tooltip={MEETING_TOOLTIPS.topic_participants} />
                      <ParticipantPicker
                        profiles={options.profiles || []}
                        selectedIds={topic.topic_participant_ids || []}
                        onToggle={(profileId) => handleToggleTopicParticipant(index, profileId)}
                        disabled={loading || saving}
                        emptyLabel="Seleccionar participantes"
                        getOptionMeta={(user) => (
                          meetingForm.participant_ids.includes(user.id)
                            ? { disabled: true, reason: 'Ya participa de la reunion general' }
                            : null
                        )}
                      />
                      {topic.topic_participant_ids?.some((profileId) => meetingForm.participant_ids.includes(profileId)) ? (
                        <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-amber-600">
                          Los participantes generales no pueden repetirse dentro del tema.
                        </p>
                      ) : null}
                    </div>

                    <div className="xl:col-span-3">
                      <CrmFieldLabel label="Tiempo del tema (min)" tooltip={MEETING_TOOLTIPS.topic_minutes} />
                      <input
                        type="number"
                        min="1"
                        value={topic.topic_minutes}
                        onChange={(event) => handleTopicChange(index, 'topic_minutes', event.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-[11px] font-black outline-none focus:border-[#51A7FD]"
                        placeholder="Opcional"
                        disabled={loading || saving}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
                    <div className="xl:col-span-8">
                      <CrmFieldLabel label="Notas del tema" tooltip={MEETING_TOOLTIPS.topic_notes} />
                      <textarea
                        value={topic.notes}
                        onChange={(event) => handleTopicChange(index, 'notes', event.target.value)}
                        className="w-full min-h-[88px] resize-y bg-white border border-slate-200 rounded-xl px-4 py-3 text-[11px] font-bold outline-none focus:border-[#51A7FD]"
                        placeholder="Objetivo puntual o resumen esperado del tema"
                        disabled={loading || saving}
                      />
                    </div>

                    <div className="xl:col-span-4">
                      <CrmFieldLabel label="Tratado" tooltip={MEETING_TOOLTIPS.treated} className="mb-2" />
                      <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <input
                          type="checkbox"
                          checked={Boolean(topic.is_treated)}
                          onChange={(event) => handleTopicChange(index, 'is_treated', event.target.checked)}
                          className="accent-[#51A7FD]"
                          disabled={loading || saving}
                        />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Se trato en la reunion</span>
                      </label>
                      <p className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                        Si no completas tiempo, el restante de la reunion se distribuye automaticamente entre los temas tratados.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => setMeetingForm((current) => ({ ...current, topics: [...current.topics, buildTopicDraft()] }))}
            className="sm:w-auto w-full rounded-2xl bg-[#0B2A36] text-white px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] shadow-lg flex items-center justify-center gap-2"
            disabled={loading || saving}
          >
            <Plus size={16} />
            Agregar tema
          </button>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => navigate('/sistema/agenda')}
              className="sm:w-auto w-full rounded-2xl bg-white border border-slate-200 text-slate-500 px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em]"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || loading}
              className="sm:w-auto w-full rounded-2xl bg-[#3FF2AA] text-[#0B2A36] px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving ? <RefreshCcw size={16} className="animate-spin" /> : <Plus size={16} />}
              Crear reunion
            </button>
          </div>
        </div>
      </form>

      {meetingParticipantConflict ? (
        <div className="fixed inset-0 z-50 bg-slate-900/35 backdrop-blur-[1px] flex items-center justify-center px-4">
          <div className="w-full max-w-lg rounded-3xl border border-amber-200 bg-white shadow-2xl overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                Participante ya asignado a temas
              </p>
              <h3 className="mt-1 text-lg font-black text-slate-800">{meetingParticipantConflict.profileName}</h3>
              <p className="mt-2 text-[11px] font-bold text-slate-600">
                Si lo agregas a la reunion general se va a eliminar de estos temas:
              </p>
            </div>

            <div className="px-5 py-4">
              <ul className="space-y-2">
                {meetingParticipantConflict.affectedTopics.map((topicName) => (
                  <li key={topicName} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700">
                    {topicName}
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={cancelMeetingParticipantConflict}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={confirmMeetingParticipantConflict}
                  className="rounded-2xl bg-amber-500 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white"
                >
                  Si, borrar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {topicDeleteConfirm ? (
        <div className="fixed inset-0 z-50 bg-slate-900/35 backdrop-blur-[1px] flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-3xl border border-red-200 bg-white shadow-2xl overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-red-700">
                Eliminar tema
              </p>
              <h3 className="mt-1 text-lg font-black text-slate-800">{topicDeleteConfirm.label}</h3>
              <p className="mt-2 text-[11px] font-bold text-slate-600">
                Esta accion va a quitar el tema de la reunion.
              </p>
            </div>

            <div className="px-5 py-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700">
                Confirma si quieres eliminar este tema.
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={cancelTopicRemoval}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmTopicRemoval}
                  className="rounded-2xl bg-red-500 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

